import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type AvailabilityResponse = {
  date: string;
  openMin: number;
  closeMin: number;
  slotMin: number;
  courts: {
    id: number;
    name: string;
    slots: { startMin: number; endMin: number; available: boolean }[];
  }[];
};

export function useAvailability(date: string | undefined) {
  return useQuery<AvailabilityResponse>({
    queryKey: ["availability", date],
    queryFn: async () => {
      const res = await fetch(`/api/availability?date=${date}`);
      if (!res.ok) throw new Error("Failed to load availability");
      return (await res.json()) as AvailabilityResponse;
    },
    enabled: !!date,
  });
}

export function useBook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      courtId: number;
      date: string;
      startMin: number;
      name: string;
      email: string;
      phone?: string;
    }) => {
      const res = await fetch("/api/book", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Booking failed");
      return data as { success: boolean; code: string };
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["availability", variables.date] });
    },
  });
}

export function useCancelBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { code: string; email: string }) => {
      const res = await fetch("/api/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Cancel failed");
      return data as { success: boolean };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-bookings"] });
    },
  });
}

export type Booking = {
  id: number;
  courtId: number;
  date: string;
  startMin: number;
  endMin: number;
  name: string;
  email: string;
  phone: string | null;
  code: string;
  type: string;
  status: string;
  createdAt: number;
};

export function useMyBookings(params: { code?: string; email?: string }) {
  const { code, email } = params;
  return useQuery<{ bookings: Booking[] }>({
    queryKey: ["my-bookings", code, email],
    queryFn: async () => {
      const res = await fetch(
        `/api/my-bookings?code=${code}&email=${encodeURIComponent(email || "")}`
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Load failed");
      return data as { bookings: Booking[] };
    },
    enabled: !!code && !!email,
  });
}
